import { FormControl, MenuItem, Select } from "@mui/material";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { RobotoTypography } from "../RobotoTypography";

import { EventSourceType, EventScope, EventDataEntityType } from "./scope";

interface EventScopeFormProps {
  sourceOptions: Array<EventScope["source"]>;
  /** Offer "This Session". Set only when the workspace shows a session and the org has session events. */
  includeSession?: boolean;
  /**
   * Offer the dataset event scopes (all datasets, files, topics or message
   * paths). Unset only in an org whose dataset events cannot be written.
   */
  includeDataScopes?: boolean;
}

const dataEntityOptions = [
  EventDataEntityType.AllDatasets,
  EventDataEntityType.AllFiles,
  EventDataEntityType.AllTopics,
  EventDataEntityType.AllMessagePaths,
];

export function EventScopeForm({
  sourceOptions,
  includeSession = false,
  includeDataScopes = true,
}: EventScopeFormProps) {
  const form = useFormContext<{ scope: EventScope }>();
  const dataEntityType = useWatch({
    control: form.control,
    name: "scope.dataEntity.type",
  });
  const options = [
    ...(includeSession ? [EventDataEntityType.ThisSession] : []),
    ...(includeDataScopes ? dataEntityOptions : []),
  ];
  // A "This Session" event is about all of the session's data, so the source
  // picker (which panels the selection came from) does not apply.
  const showSource = dataEntityType !== EventDataEntityType.ThisSession;
  return (
    <section>
      <RobotoTypography
        component={"h3"}
        sx={{ fontWeight: 500, fontSize: "1rem", marginBottom: "0.5rem" }}
      >
        Scope event to
      </RobotoTypography>
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <FormControl size="small">
          <Controller
            name="scope.dataEntity.type"
            control={form.control}
            render={({ field }) => {
              return (
                <Select {...field} id="dataEntity">
                  {options.map((dataEntity) => (
                    <MenuItem key={dataEntity} value={dataEntity}>
                      {dataEntity}
                    </MenuItem>
                  ))}
                </Select>
              );
            }}
          ></Controller>
        </FormControl>
        {showSource && (
          <>
            in
            <FormControl size="small">
              <Controller
                name="scope.source"
                control={form.control}
                render={({ field }) => {
                  return (
                    <Select
                      id="source"
                      onChange={(event) => {
                        // Note(Pratik) We're searching for the source in the source options because we
                        // want to use the entire source object instead of just the source type.
                        const source = sourceOptions.find(
                          (source) =>
                            source.type ===
                            (event.target.value as EventSourceType),
                        );

                        if (!source) {
                          // This shouldn't ever happen because the only selectable inputs are the source options
                          throw new Error(
                            "Selected source is not in available source options",
                          );
                        }

                        field.onChange(source);
                      }}
                      value={field.value.type}
                    >
                      {sourceOptions.map((source) => (
                        <MenuItem key={source.type} value={source.type}>
                          {source.type}
                        </MenuItem>
                      ))}
                    </Select>
                  );
                }}
              ></Controller>
            </FormControl>
          </>
        )}
      </div>
    </section>
  );
}
